import {
	Box,
	Checkbox,
	FormControl,
	FormControlLabel,
	FormHelperText,
	Grid,
	InputLabel,
	MenuItem,
	Select,
	Typography,
} from "@mui/material";
import React, { FC, useEffect, useRef, useState } from "react";
import { useStyles } from "src/theme/styles";
import { useTranslation } from "react-i18next";
import {
	TextEntryPoint,
	WebchatEntryPointCallingMappingsContainerProps,
} from "src/types/textEntryPoints";
import { BotApplicationInstance } from "src/types/botApplicationInstance";
import { botApplicationInstanceService } from "src/services/botApplicationInstances";
import {
	HTTPSTATUSCODES,
	MessagingEntryPointTranslationKeys,
} from "src/utils/constants";
import { mapBotApplicationInstances } from "src/utils/botApplicationHelper";
import { useFormikContext } from "formik";

interface CallingRowProps {
	checkboxName: string;
	checkboxLabel: string;
	checked: boolean;
	dropdownName: string;
	dropdownValue: string;
}

const WebchatEntryPointCallingContainer: FC<WebchatEntryPointCallingMappingsContainerProps> =
	React.forwardRef((props, ref) => {
		const mappingProps = props[
			"entryPointMappingsContainerProps"
		] as WebchatEntryPointCallingMappingsContainerProps;
		const classes = useStyles();
		const { t } = useTranslation();
		const { setFieldValue } = useFormikContext<TextEntryPoint>();
		const botAppInstances = useRef([]);
		const [botApplicationInstances, setBotApplicationInstances] = useState<
			BotApplicationInstance[]
		>([]);

		useEffect(() => {
			getBotApplicationInstances();
		}, []);

		const getBotApplicationInstances = async () => {
			try {
				const response =
					await botApplicationInstanceService.getAllBotApplicationInstances();
				if (response?.status === HTTPSTATUSCODES.StatusCodeSuccess) {
					const unifyBotAppInstances = mapBotApplicationInstances(
						response.data,
						botAppInstances.current
					).filter((element) => element.acsResourceId);

					setBotApplicationInstances(unifyBotAppInstances);
				}
			} catch (err) {
				console.error(err);
			}
		};

		const getSelectMenuItem = (data: any) => {
			return data?.map((type) => (
				<MenuItem key={type.objectId} value={type.objectId}>
					{type.displayName}
				</MenuItem>
			));
		};

		const renderCallingRow = ({
			checkboxName,
			checkboxLabel,
			checked,
			dropdownName,
			dropdownValue,
		}: CallingRowProps) => {
			const hasError = Boolean(
				mappingProps.touched[dropdownName] &&
					mappingProps.errors[dropdownName]
			);

			return (
				<Grid item container xs={12} spacing={2}>
					<Grid
						item
						xs={4}
						className={classes.entryPointContainerInfo}
					>
						<FormControlLabel
							control={
								<Checkbox
									color="primary"
									checked={checked}
									name={checkboxName}
									onChange={(
										event: React.ChangeEvent<HTMLInputElement>,
										isChecked: boolean
									) => {
										mappingProps.handleChange(
											event,
											isChecked
										);
										if (!isChecked) {
											setFieldValue(dropdownName, "");
										}
									}}
								/>
							}
							label={
								<Typography
									color="textSecondary"
									sx={{ whiteSpace: "nowrap" }}
								>
									{checkboxLabel}
								</Typography>
							}
							labelPlacement="end"
							sx={{ mb: 1 }}
						/>
					</Grid>
					<Grid item xs={4}>
						<FormControl fullWidth>
							<InputLabel
								id={`${dropdownName}-select-label`}
								className={classes.selectComponentLabel}
							>
								{t("BotApplicationInstance.VoiceEntryPoints")}
							</InputLabel>
							<Select
								error={hasError}
								value={dropdownValue || ""}
								disabled={!checked}
								label={t(
									"BotApplicationInstance.VoiceEntryPoints"
								)}
								onChange={(event) => {
									mappingProps.handleChange(event);
								}}
								size="small"
								name={dropdownName}
								className={classes.selectComponent}
								data-testid={dropdownName}
							>
								{getSelectMenuItem(botApplicationInstances)}
							</Select>
							{hasError && (
								<FormHelperText error>
									{t(
										MessagingEntryPointTranslationKeys.Required
									)}
								</FormHelperText>
							)}
						</FormControl>
					</Grid>
				</Grid>
			);
		};

		return (
			<Box className={classes.entryPointSectionContainer}>
				<Grid item container xs={12} spacing={2}>
					<Grid
						item
						xs={12}
						className={classes.entryPointContainerInfo}
					>
						{t("TextEntryPoint.CallingExplanations")}
					</Grid>
					{renderCallingRow({
						checkboxName: "callingEnabled",
						checkboxLabel: t("TextEntryPoint.EnableCustomerCalling"),
						checked: mappingProps.callingEnabled,
						dropdownName: "callingApplicationInstanceId",
						dropdownValue: mappingProps.callingApplicationInstanceId,
					})}
					{/* The agent picks the calling resource account at call time, so this row is just the toggle. */}
					<Grid item container xs={12} spacing={2}>
						<Grid
							item
							xs={4}
							className={classes.entryPointContainerInfo}
						>
							<FormControlLabel
								control={
									<Checkbox
										color="primary"
										checked={mappingProps.agentCallingEnabled}
										name="agentCallingEnabled"
										onChange={(
											event: React.ChangeEvent<HTMLInputElement>,
											isChecked: boolean
										) =>
											mappingProps.handleChange(event, isChecked)
										}
									/>
								}
								label={
									<Typography
										color="textSecondary"
										sx={{ whiteSpace: "nowrap" }}
									>
										{t("TextEntryPoint.EnableAgentCalling")}
									</Typography>
								}
								labelPlacement="end"
								sx={{ mb: 1 }}
							/>
						</Grid>
					</Grid>
				</Grid>
			</Box>
		);
	});

export default WebchatEntryPointCallingContainer;
